<template>
  <div class="livechat-widget">
    <!-- 自定义触发器插槽 -->
    <div v-if="$slots.trigger" @click="handleChatClick" class="cursor-pointer">
      <slot name="trigger" :loading="loading" :initializing="isInitializing" :error="hasError" />
    </div>
    
    <!-- 默认客服按钮 -->
    <button 
      v-else-if="!loading" 
      @click="handleChatClick"
      :disabled="isInitializing"
      class="py-2 text-gray-600 hover:text-gray-900 rounded-full transition-colors relative"
      :class="{
        'opacity-50 cursor-not-allowed': isInitializing,
        'animate-pulse': isInitializing
      }"
      :title="buttonTitle"
    >
      <img style="width: 60px;" src="../../assets/images/message.png">
      
      <!-- 加载指示器 -->
      <div 
        v-if="isInitializing" 
        class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full"
      >
        <div class="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </button>

    <!-- 加载状态 -->
    <div 
      v-else
      class="p-2 text-gray-400 rounded-full"
      title="正在加载客服配置..."
    >
      <n-icon size="20">
        <MessageCircleIcon />
      </n-icon>
    </div>

    <!-- 错误提示 (仅在开发环境显示) -->
    <div 
      v-if="hasError && isDevelopment" 
      class="absolute top-full right-0 mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700 whitespace-nowrap z-50"
    >
      {{ error?.message || '客服系统错误' }}
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { NIcon } from 'naive-ui'
import { MessageCircle as MessageCircleIcon } from '@vicons/tabler'
import { useLiveChatStore } from '@/stores/livechat'
import { useMessage } from 'naive-ui'

// Props定义
interface Props {
  /** 是否自动加载配置 */
  autoLoad?: boolean
  /** 点击时是否自动打开聊天窗口 */
  autoOpen?: boolean
  /** 是否显示错误提示 */
  showError?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  autoLoad: true,
  autoOpen: true,
  showError: false
})

// Emits定义
interface Emits {
  /** 聊天窗口打开事件 */
  chatOpened: []
  /** 聊天窗口关闭事件 */
  chatClosed: []
  /** 初始化成功事件 */
  initialized: []
  /** 错误事件 */
  error: [error: string]
}

const emit = defineEmits<Emits>()

// 组合式API
const liveChatStore = useLiveChatStore()
const message = useMessage()

// 响应式数据
const isInitializing = ref(false)
const initializationAttempted = ref(false)

// 计算属性
const config = computed(() => liveChatStore.config)
const loading = computed(() => liveChatStore.loading)
const error = computed(() => liveChatStore.error)
const isReady = computed(() => liveChatStore.isReady)
const isAvailable = computed(() => liveChatStore.isAvailable)

const hasError = computed(() => !!error.value)

const isDevelopment = computed(() => {
  return import.meta.env.DEV || props.showError
})

const buttonTitle = computed(() => {
  if (isInitializing.value) return '正在初始化客服系统...'
  if (loading.value) return '正在加载客服配置...'
  if (hasError.value) return '客服系统暂时不可用，点击重试'
  if (config.value.enabled === false) return '客服功能未启用，点击获取帮助'
  if (isReady.value) return '联系客服'
  return '客服支持'
})

// 方法
const loadConfig = async (): Promise<void> => {
  try {
    console.log('开始加载LiveChat配置...')
    await liveChatStore.loadConfig()
    console.log('LiveChat配置加载成功:', config.value)
  } catch (err) {
    console.error('LiveChat配置加载失败:', err)
    const errorMessage = err instanceof Error ? err.message : '加载客服配置失败'
    emit('error', errorMessage)
    
    if (props.showError) {
      message.error(errorMessage)
    }
  }
}

const initializeChat = async (): Promise<void> => {
  // 不再用 initializationAttempted 鎖死重試，依賴 store 內部 in-flight Promise 復用
  try {
    isInitializing.value = true
    initializationAttempted.value = true

    console.log('开始初始化LiveChat...')

    // 获取当前用户信息
    const currentUser = liveChatStore.getCurrentUserForLiveChat()

    if (currentUser) {
      console.log('使用已登录用户信息初始化LiveChat:', {
        userId: currentUser.id,
        userName: currentUser.name,
        userEmail: currentUser.email
      })
    } else {
      console.log('使用访客模式初始化LiveChat')
    }

    // 初始化LiveChat（store 內部 in-flight Promise 復用，併發點擊共享）
    await liveChatStore.initializeChat({
      user: currentUser,
      autoOpen: false // 不自动打开，等用户点击
    })

    console.log('LiveChat初始化成功1')

    emit('initialized')

  } catch (err) {
    console.error('LiveChat初始化失败:', err)
    const errorMessage = err instanceof Error ? err.message : 'LiveChat初始化失败'
    emit('error', errorMessage)

    // 重置状态以允许重试
    initializationAttempted.value = false

    if (props.showError) {
      message.error(errorMessage)
    }

    // 必須 rethrow，讓外層的真重試和真錯誤分支能跑到
    throw err
  } finally {
    isInitializing.value = false
  }
}



const handleChatClick = async (): Promise<void> => {
  try {
    console.log('用户点击客服按钮')

    // 如果配置正在加載，等待加載完成
    if (loading.value) {
      console.log('正在加载配置，请稍候...')
      message.info('Connecting to the customer service system...')
      return
    }

    // 如果配置加載失敗或未啟用，嘗試重新加載
    if (config.value.enabled === undefined || config.value.enabled === false) {
      console.log('配置未启用或加载失败，尝试重新加载...')

      try {
        await loadConfig()
        console.log('重新加载配置成功:', config.value)

        if (!config.value.enabled) {
          message.warning('The customer service function is temporarily unavailable. Please contact the system administrator.')
          return
        }
      } catch (reloadError) {
        console.error('重新加载配置失败:', reloadError)
        message.error('Unable to connect to the customer service system, please check your network connection.')
        return
      }
    }

    // 如果還沒就緒，等待 init（store 內部會復用 in-flight Promise）
    if (!isReady.value) {
      console.log('LiveChat 尚未就緒，等待初始化...')

      try {
        await initializeChat()
      } catch (initError) {
        // SDK 加載已內含重試，到這裡都失敗的話直接報錯
        console.error('LiveChat 初始化最終失敗:', initError)
        emit('error', initError instanceof Error ? initError.message : 'init failed')
        message.error('The customer service system is temporarily unavailable, please try again later.')
        return
      }
    }

    // 此時應該已就緒
    if (isReady.value && props.autoOpen) {
      console.log('打开LiveChat聊天窗口...')
      liveChatStore.openChatWindow()
      document.getElementById('chat-widget-container')?.style.setProperty('display', 'block');

      emit('chatOpened')
      console.log('聊天窗口已打开')
    } else if (!isReady.value) {
      console.warn('LiveChat is not ready after initialization', {
        isReady: isReady.value,
        status: liveChatStore.status,
        error: error.value
      })
      throw new Error('LiveChat is not ready, unable to open the chat window.')
    }

  } catch (err) {
    console.error('打开聊天窗口失败:', err)
    const errorMessage = err instanceof Error ? err.message : 'Failed to open customer service chat'
    emit('error', errorMessage)

    // 显示用户友好的错误信息
    message.error('The customer service system is temporarily unavailable, please try again later.')
  }
}



// 监听LiveChat窗口状态变化
const setupEventListeners = (): void => {
  // 这里可以添加对LiveChat事件的监听
  // 由于LiveChat的事件监听已经在store中处理，这里主要是组件级别的事件处理
}

// 清理函数
const cleanup = (): void => {
  // 清理事件监听器等
  console.log('LiveChatWidget组件清理')
}

// 生命周期
onMounted(async () => {
  console.log('LiveChatWidget组件已挂载')

  // 设置事件监听
  setupEventListeners()

  // 如果配置为自动加载，则加载配置
  if (props.autoLoad) {
    try {
      console.log('开始自动加载LiveChat配置...')
      await loadConfig()
      console.log('配置加载完成，当前配置:', config.value)

      // 如果配置已啟用，立即預初始化（不阻塞）
      // 預初始化失敗不影響後續用戶點擊（store 內部已有 in-flight 復用 + SDK 重試）
      if (config.value.enabled && config.value.licenseId) {
        console.log('配置已启用，开始预初始化...')
        initializeChat().catch(err => {
          console.warn('预初始化LiveChat失败（用戶點擊時會自動重試）:', err)
        })
      } else {
        console.log('LiveChat配置未启用或License ID缺失:', {
          enabled: config.value.enabled,
          licenseId: config.value.licenseId
        })
      }
    } catch (error) {
      console.error('自动加载配置失败:', error)
    }
  }
})

onUnmounted(() => {
  cleanup()
})

// 暴露给父组件的方法
defineExpose({
  loadConfig,
  initializeChat,
  handleChatClick,
  openChat: handleChatClick,
  isReady: computed(() => isReady.value),
  isAvailable: computed(() => isAvailable.value),
  hasError: computed(() => hasError.value),
  error: computed(() => error.value)
})
</script>

<style scoped>
.livechat-widget {
  position: relative;
  display: inline-block;
}

/* 加载动画 */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* 脉冲动画 */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .livechat-widget {
    /* 移动端优化 */
  }
}
</style>